import axios from "axios";

const state = {
  purchase_orders: [],
  loading: false,
};

const getters = {
  purchase_orders: (state) => state.purchase_orders,
  loading: (state) => state.loading,
};

const actions = {
  async get_purchase_orders({ commit }) {
    commit("SET_LOADING", true);
    try {
      const response = await axios.get("purchase_orders", {
        params: {
          with: "canvass.customer_purchase_order.customer,items.canvass_item,supplier,created_by",
        },
      });
      commit("SET_PURCHASE_ORDERS", response.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching purchase orders:", error);
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async add_purchase_order({ commit }, payload) {
    try {
      const response = await axios.post("purchase_order", payload);
      commit("ADD_PURCHASE_ORDER", response.data);
      return response.data;
    } catch (error) {
      console.error("Error adding purchase order:", error);
      throw error;
    }
  },

  async update_purchase_order({ commit }, payload) {
    try {
      const response = await axios.put(`purchase_order/${payload.id}`, payload);
      commit("UPDATE_PURCHASE_ORDER", response.data);
      return response.data;
    } catch (error) {
      console.error("Error updating purchase order:", error);
      throw error;
    }
  },

  async delete_purchase_order({ commit }, id) {
    try {
      await axios.delete(`purchase_order/${id}`);
      commit("DELETE_PURCHASE_ORDER", id);
    } catch (error) {
      console.error("Error deleting purchase order:", error);
      throw error;
    }
  },

  async update_status({ commit }, { id, status }) {
    try {
      const response = await axios.put(`purchase_order/${id}/status`, {
        status,
      });
      commit("UPDATE_PURCHASE_ORDER", response.data);
      return response.data;
    } catch (error) {
      console.error("Error updating purchase order status:", error);
      throw error;
    }
  },

  // Set / edit the Batch Import code linked to a PO. Server renames the
  // linked ImportBatch or creates+links one when the PO has none. (JMB-234)
  async update_batch_import({ commit }, { id, batch_description }) {
    try {
      const response = await axios.put(`purchase_order/${id}/batch_import`, {
        batch_description,
      });
      commit("UPDATE_PURCHASE_ORDER", response.data);
      return response.data;
    } catch (error) {
      console.error("Error updating purchase order batch import:", error);
      throw error;
    }
  },

  async mark_completed({ commit }, id) {
    try {
      const response = await axios.put(`purchase_order/${id}/mark-completed`);
      commit("UPDATE_PURCHASE_ORDER", response.data);
      return response.data;
    } catch (error) {
      console.error("Error marking purchase order as completed:", error);
      throw error;
    }
  },

  async update_purchase_order_item(_context, { itemId, data }) {
    try {
      const response = await axios.put(`purchase_order_item/${itemId}`, data);
      return response.data;
    } catch (error) {
      console.error("Error updating purchase order item:", error);
      throw error;
    }
  },

  async combine_purchase_orders({ commit }, { po_ids, combined_items }) {
    try {
      const response = await axios.post("purchase_order/combine", {
        po_ids,
        combined_items,
      });

      // Remove the old POs
      po_ids.forEach((id) => {
        commit("DELETE_PURCHASE_ORDER", id);
      });

      // Add the new combined PO
      commit("ADD_PURCHASE_ORDER", response.data);

      return response.data;
    } catch (error) {
      console.error("Error combining purchase orders:", error);
      throw error;
    }
  },

  async update_po_item({ commit }, { po_id, item_id, item_data }) {
    try {
      const response = await axios.put(
        `purchase_order/${po_id}/item/${item_id}`,
        item_data
      );

      // Update the purchase order in state
      commit("UPDATE_PURCHASE_ORDER", response.data);

      return response.data;
    } catch (error) {
      console.error("Error updating PO item:", error);
      throw error;
    }
  },

  async remove_po_item({ commit }, { po_id, item_id }) {
    try {
      const response = await axios.delete(
        `purchase_order/${po_id}/item/${item_id}`
      );

      // Update the purchase order in state
      commit("UPDATE_PURCHASE_ORDER", response.data);

      return response.data;
    } catch (error) {
      console.error("Error removing PO item:", error);
      throw error;
    }
  },

  async add_item_to_po({ commit }, { po_id, item_data }) {
    try {
      const response = await axios.post(
        `purchase_order/${po_id}/item`,
        item_data
      );

      // Update the purchase order in state
      commit("UPDATE_PURCHASE_ORDER", response.data.purchase_order);

      return response.data.item;
    } catch (error) {
      console.error("Error adding item to PO:", error);
      throw error;
    }
  },

  async create_direct_purchase({ commit }, payload) {
    try {
      const response = await axios.post("purchase_order/direct", payload);
      commit("ADD_PURCHASE_ORDER", response.data);
      return response.data;
    } catch (error) {
      console.error("Error creating direct purchase:", error);
      throw error;
    }
  },

  async add_status_history({ commit }, { po_id, history_entry }) {
    try {
      const response = await axios.post(
        `purchase_order/${po_id}/status-history`,
        history_entry
      );

      // Update the purchase order in state
      commit("UPDATE_PURCHASE_ORDER", response.data);

      return response.data;
    } catch (error) {
      console.error("Error adding status history:", error);
      throw error;
    }
  },
};

const mutations = {
  SET_LOADING(state, loading) {
    state.loading = loading;
  },

  SET_PURCHASE_ORDERS(state, purchase_orders) {
    state.purchase_orders = purchase_orders;
  },

  ADD_PURCHASE_ORDER(state, purchase_order) {
    state.purchase_orders.unshift(purchase_order);
  },

  UPDATE_PURCHASE_ORDER(state, updatedPurchaseOrder) {
    const index = state.purchase_orders.findIndex(
      (po) => po.id == updatedPurchaseOrder.id
    );
    if (index !== -1) {
      state.purchase_orders.splice(index, 1, updatedPurchaseOrder);
    }
  },

  DELETE_PURCHASE_ORDER(state, id) {
    state.purchase_orders = state.purchase_orders.filter((po) => po.id != id);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
